import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { environment } from '../../../environments/environment';
import { devLog, devError } from '@core';

/**
 * Emulator Health Check Service
 *
 * **Purpose**: Verify Firebase emulators are running in development mode
 * Prevents confusing "Session Expired" errors when Functions emulator is down
 *
 * **Design Decision**: Only runs in development (!production && useEmulators)
 * Production deployments skip these checks entirely (tree-shaken out)
 *
 * **CORS Handling**: The environment config may use LAN IP (e.g., 10.0.0.73)
 * for mobile device testing, but health checks from browser need localhost
 * to avoid CORS errors. This service detects browser hostname and uses
 * localhost URLs when running locally.
 *
 * **Usage**: Call checkEmulatorsHealth() on app startup
 *
 * **Related**:
 * - Issue: Functions emulator timeout causes createUserProfile to not run
 * - Symptom: "Session Expired" errors after OTP verification
 * - Fix: This service detects emulator issues early with helpful error messages
 */
@Injectable({
  providedIn: 'root'
})
export class EmulatorHealthCheckService {
  private toastController = inject(ToastController);

  /**
   * Get the appropriate emulator host for health checks
   *
   * When running in browser on localhost, use localhost URLs to avoid CORS issues.
   * The environment config may have LAN IP for mobile device testing, but
   * health checks from browser need localhost.
   *
   * @param configuredUrl - URL from environment config (may be LAN IP)
   * @returns URL with localhost if running locally, otherwise unchanged
   */
  private getHealthCheckUrl(configuredUrl: string): string {
    // If browser is running on localhost, use localhost for health checks
    // to avoid CORS errors when environment config uses LAN IP
    const isLocalBrowser = typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (isLocalBrowser) {
      // Extract port from configured URL and use localhost
      try {
        const url = new URL(configuredUrl);
        return `http://localhost:${url.port}`;
      } catch {
        // If URL parsing fails, return as-is
        return configuredUrl;
      }
    }

    return configuredUrl;
  }

  /**
   * Check health of all Firebase emulators
   * Shows toast warnings if any emulators are unreachable
   *
   * @returns Promise<boolean> - true if all emulators healthy, false otherwise
   */
  async checkEmulatorsHealth(): Promise<boolean> {
    // Only run health checks in development mode with emulators enabled
    if (environment.production || !environment.useEmulators) {
      return true;
    }

    devLog('[EmulatorHealthCheck] Checking emulator health...');

    const checks = [
      this.checkAuthEmulator(),
      this.checkFirestoreEmulator(),
      this.checkFunctionsEmulator()
    ];

    const results = await Promise.allSettled(checks);

    const allHealthy = results.every(r => r.status === 'fulfilled' && r.value);

    if (!allHealthy) {
      devError('[EmulatorHealthCheck] Some emulators are unhealthy');
      await this.showEmulatorWarning();
    } else {
      devLog('[EmulatorHealthCheck] All emulators healthy ✓');
    }

    return allHealthy;
  }

  /**
   * Check if Firebase Auth emulator is reachable
   */
  private async checkAuthEmulator(): Promise<boolean> {
    if (!environment.emulators?.auth) {
      return true; // Skip if not configured
    }

    try {
      const healthCheckUrl = this.getHealthCheckUrl(environment.emulators.auth);
      devLog('[EmulatorHealthCheck] Checking Auth at', healthCheckUrl);

      const response = await fetch(`${healthCheckUrl}/emulator/v1/projects/checkerfly-5ec18/config`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });

      if (!response.ok) {
        devError('[EmulatorHealthCheck] Auth emulator returned error', { status: response.status });
        return false;
      }

      devLog('[EmulatorHealthCheck] Auth emulator healthy ✓');
      return true;
    } catch (error) {
      devError('[EmulatorHealthCheck] Auth emulator unreachable', { error });
      return false;
    }
  }

  /**
   * Check if Firestore emulator is reachable
   */
  private async checkFirestoreEmulator(): Promise<boolean> {
    if (!environment.emulators?.firestore) {
      return true; // Skip if not configured
    }

    try {
      const healthCheckUrl = this.getHealthCheckUrl(environment.emulators.firestore);
      devLog('[EmulatorHealthCheck] Checking Firestore at', healthCheckUrl);

      // Firestore emulator doesn't have a dedicated health endpoint
      // Try to hit the root to verify it's running
      await fetch(healthCheckUrl, {
        method: 'GET',
        signal: AbortSignal.timeout(3000)
      });

      // Any response (even 404) means emulator is running
      devLog('[EmulatorHealthCheck] Firestore emulator healthy ✓');
      return true;
    } catch (error) {
      devError('[EmulatorHealthCheck] Firestore emulator unreachable', { error });
      return false;
    }
  }

  /**
   * Check if Functions emulator is reachable
   * This is the most critical check - if Functions are down, user creation fails
   *
   * Note: Functions emulator has no health endpoint - root returns 404.
   * We use 'no-cors' mode and check if connection succeeds (not network error).
   * The 404 in browser Network tab is expected and unavoidable.
   */
  private async checkFunctionsEmulator(): Promise<boolean> {
    if (!environment.emulators?.functions) {
      return true; // Skip if not configured
    }

    try {
      const healthCheckUrl = this.getHealthCheckUrl(environment.emulators.functions);
      devLog('[EmulatorHealthCheck] Checking Functions at', healthCheckUrl, '(404 in Network tab is expected)');

      // Use 'no-cors' mode because Functions emulator root doesn't return
      // Access-Control-Allow-Origin header. We can't read the response,
      // but a successful fetch (no network error) means emulator is running.
      // Note: Browser Network tab will show 404 - this is expected behavior.
      await fetch(healthCheckUrl, {
        method: 'HEAD', // Use HEAD to minimize response size
        mode: 'no-cors',
        signal: AbortSignal.timeout(5000) // Longer timeout for Functions (they can be slow to init)
      });

      devLog('[EmulatorHealthCheck] Functions emulator healthy ✓');
      return true;
    } catch (error) {
      devError('[EmulatorHealthCheck] Functions emulator unreachable', { error });
      return false;
    }
  }

  /**
   * Show toast warning when emulators are unhealthy
   */
  private async showEmulatorWarning(): Promise<void> {
    const toast = await this.toastController.create({
      header: 'Emulator Warning',
      message: 'Some Firebase emulators are not running. Authentication may fail. Check the terminal for emulator status.',
      color: 'warning',
      duration: 8000,
      position: 'top',
      buttons: [
        {
          text: 'Dismiss',
          role: 'cancel'
        }
      ]
    });

    await toast.present();
  }
}
